import { useState, useEffect } from "react";
import { useLoaderData, useFetcher } from "react-router-dom";
import toast from "react-hot-toast";
import { Header } from "../../components/layout/Header";
import { Button, LinkButton } from "../../components/common";
import { useAuth } from "../../hooks/useAuth";
import type { NetworkMembershipsData } from "../../routes/loaders";
import type { ActionResult } from "../../routes/actions";
import type { NetworkMembershipResponse } from "../../types/api";
import styles from "./NetworkMembershipsPage.module.css";

type StatusFilter = "all" | "active" | "pending" | "departed";

export default function NetworkMembershipsPage() {
  const { network, tiers, memberships, organizationId } = useLoaderData() as NetworkMembershipsData;
  const { user } = useAuth();

  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [confirmAction, setConfirmAction] = useState<{
    type: "leave" | "reject";
    membership: NetworkMembershipResponse;
  } | null>(null);
  const [editingSplit, setEditingSplit] = useState<string | null>(null);
  const [splitValue, setSplitValue] = useState("");

  const inviteFetcher = useFetcher();
  const acceptFetcher = useFetcher();
  const rejectFetcher = useFetcher();
  const leaveFetcher = useFetcher();
  const splitFetcher = useFetcher();

  const userOrg = user?.organizations.find((o) => o.id === organizationId);
  const isAdmin = userOrg?.role === "owner" || userOrg?.role === "admin";

  const filteredMemberships = memberships.filter((m) => {
    if (statusFilter === "all") return true;
    return m.status === statusFilter;
  });

  const activeMembers = memberships.filter((m) => m.status === "active");
  const pendingMembers = memberships.filter((m) => m.status === "pending");

  // Handle fetcher results
  useEffect(() => {
    const fetchers = [
      { fetcher: inviteFetcher, successMsg: "Invitation sent!" },
      { fetcher: acceptFetcher, successMsg: "Membership accepted!" },
      { fetcher: rejectFetcher, successMsg: "Membership rejected." },
      { fetcher: leaveFetcher, successMsg: "Removed from network." },
      { fetcher: splitFetcher, successMsg: "Revenue split updated!" },
    ];

    for (const { fetcher, successMsg } of fetchers) {
      if (fetcher.data) {
        const result = fetcher.data as ActionResult;
        if (result.success) {
          toast.success(successMsg);
          setConfirmAction(null);
          setEditingSplit(null);
        } else if (result.error) {
          toast.error(result.error);
        }
      }
    }
  }, [inviteFetcher, acceptFetcher, rejectFetcher, leaveFetcher, splitFetcher]);

  const getTierName = (tierId: string | null) => {
    if (!tierId) return "—";
    const tier = tiers.find((t) => t.id === tierId);
    return tier?.name || "Unknown";
  };

  const getStatusClass = (status: string) => {
    switch (status) {
      case "active":
        return styles.statusActive;
      case "pending":
        return styles.statusPending;
      case "departed":
        return styles.statusDeparted;
      default:
        return "";
    }
  };

  const handleSplitSave = (membership: NetworkMembershipResponse) => {
    splitFetcher.submit(
      { revenueSplitPercent: splitValue },
      {
        method: "post",
        action: `/organizations/${organizationId}/networks/${network.id}/memberships/${membership.id}/update-split`,
      },
    );
  };

  return (
    <>
      <Header />
      <div className="container">
        <div className={styles.container}>
          <div className={styles.header}>
            <h1>Members — {network.name}</h1>
            <div className={styles.headerActions}>
              <LinkButton
                to={`/organizations/${organizationId}/networks/${network.id}/settings`}
                variant="secondary"
              >
                Network Settings
              </LinkButton>
              <LinkButton to={`/organizations/${organizationId}/networks`} variant="secondary">
                Back to Networks
              </LinkButton>
            </div>
          </div>

          {/* Invite Section (for admins of invite_only or application networks) */}
          {isAdmin && (
            <div className={styles.section}>
              <h2>Invite Podcast</h2>
              <inviteFetcher.Form
                method="post"
                action={`/organizations/${organizationId}/networks/${network.id}/memberships/invite`}
                className={styles.form}
              >
                <div className={styles.formGroup}>
                  <label htmlFor="podcastId">Podcast ID</label>
                  <input
                    id="podcastId"
                    name="podcastId"
                    type="text"
                    placeholder="Enter podcast ID to invite"
                    required
                    disabled={inviteFetcher.state === "submitting"}
                  />
                  {(inviteFetcher.data as ActionResult | undefined)?.fieldErrors?.podcastId && (
                    <span className={styles.formError}>
                      {(inviteFetcher.data as ActionResult).fieldErrors!.podcastId}
                    </span>
                  )}
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="tierId">Tier (optional)</label>
                  <select id="tierId" name="tierId" disabled={inviteFetcher.state === "submitting"}>
                    <option value="">No tier</option>
                    {tiers.map((tier) => (
                      <option key={tier.id} value={tier.id}>
                        {tier.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="revenueSplitPercent">Revenue Split %</label>
                  <input
                    id="revenueSplitPercent"
                    name="revenueSplitPercent"
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    placeholder="0"
                    disabled={inviteFetcher.state === "submitting"}
                  />
                </div>

                <div className={styles.formActions}>
                  <Button
                    type="submit"
                    variant="primary"
                    loading={inviteFetcher.state === "submitting"}
                    loadingText="Inviting..."
                  >
                    Send Invite
                  </Button>
                </div>
              </inviteFetcher.Form>
            </div>
          )}

          {/* Pending Applications/Invites */}
          {isAdmin && pendingMembers.length > 0 && (
            <div className={styles.section}>
              <h2>Pending ({pendingMembers.length})</h2>
              <div className={styles.memberList}>
                {pendingMembers.map((membership) => (
                  <div key={membership.id} className={styles.memberCard}>
                    <div className={styles.memberInfo}>
                      <span className={styles.memberName}>{membership.podcastName}</span>
                      <div className={styles.memberMeta}>
                        <span className={`${styles.statusBadge} ${styles.statusPending}`}>
                          Pending
                        </span>
                        <span>Tier: {getTierName(membership.tierId)}</span>
                        <span>Split: {membership.revenueSplitPercent}%</span>
                      </div>
                    </div>
                    <div className={styles.memberActions}>
                      <acceptFetcher.Form
                        method="post"
                        action={`/organizations/${organizationId}/networks/${network.id}/memberships/${membership.id}/accept`}
                      >
                        <Button
                          type="submit"
                          variant="primary"
                          loading={acceptFetcher.state === "submitting"}
                          loadingText="..."
                        >
                          Accept
                        </Button>
                      </acceptFetcher.Form>
                      <Button
                        type="button"
                        variant="danger"
                        onClick={() => setConfirmAction({ type: "reject", membership })}
                      >
                        Reject
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Active Members */}
          <div className={styles.section}>
            <h2>Members ({activeMembers.length})</h2>

            <div className={styles.filterTabs}>
              {(["all", "active", "pending", "departed"] as StatusFilter[]).map((filter) => (
                <button
                  key={filter}
                  className={`${styles.filterTab} ${statusFilter === filter ? styles.filterTabActive : ""}`}
                  onClick={() => setStatusFilter(filter)}
                >
                  {filter.charAt(0).toUpperCase() + filter.slice(1)}
                </button>
              ))}
            </div>

            {filteredMemberships.length === 0 ? (
              <div className={styles.emptyState}>
                No {statusFilter === "all" ? "" : statusFilter} members found.
              </div>
            ) : (
              <div className={styles.memberList}>
                {filteredMemberships.map((membership) => (
                  <div key={membership.id} className={styles.memberCard}>
                    <div className={styles.memberInfo}>
                      <span className={styles.memberName}>{membership.podcastName}</span>
                      <div className={styles.memberMeta}>
                        <span
                          className={`${styles.statusBadge} ${getStatusClass(membership.status)}`}
                        >
                          {membership.status}
                        </span>
                        <span>Tier: {getTierName(membership.tierId)}</span>
                        {editingSplit === membership.id ? (
                          <div className={styles.splitEditor}>
                            <input
                              type="number"
                              className={styles.splitInput}
                              value={splitValue}
                              onChange={(e) => setSplitValue(e.target.value)}
                              min="0"
                              max="100"
                              step="0.01"
                            />
                            <span>%</span>
                            <Button
                              type="button"
                              variant="primary"
                              onClick={() => handleSplitSave(membership)}
                              loading={splitFetcher.state === "submitting"}
                              loadingText="..."
                            >
                              Save
                            </Button>
                            <Button
                              type="button"
                              variant="secondary"
                              onClick={() => setEditingSplit(null)}
                            >
                              Cancel
                            </Button>
                          </div>
                        ) : (
                          <span>
                            Split: {membership.revenueSplitPercent}%
                            {isAdmin && membership.status === "active" && (
                              <button
                                className={styles.filterTab}
                                style={{ marginLeft: "var(--spacing-2)", padding: "2px 8px" }}
                                onClick={() => {
                                  setEditingSplit(membership.id);
                                  setSplitValue(String(membership.revenueSplitPercent));
                                }}
                              >
                                Edit
                              </button>
                            )}
                          </span>
                        )}
                      </div>
                    </div>
                    {isAdmin && membership.status === "active" && (
                      <div className={styles.memberActions}>
                        <Button
                          type="button"
                          variant="danger"
                          onClick={() => setConfirmAction({ type: "leave", membership })}
                        >
                          Remove
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Confirmation Dialog */}
      {confirmAction && (
        <div
          className={styles.confirmOverlay}
          onClick={() => setConfirmAction(null)}
          onKeyDown={(e) => {
            if (e.key === "Escape") setConfirmAction(null);
          }}
          role="presentation"
        >
          <div
            className={styles.confirmDialog}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            role="presentation"
          >
            <h3>{confirmAction.type === "leave" ? "Remove Member" : "Reject Application"}</h3>
            <p>
              {confirmAction.type === "leave"
                ? `Are you sure you want to remove "${confirmAction.membership.podcastName}" from this network? This action cannot be undone.`
                : `Are you sure you want to reject the application from "${confirmAction.membership.podcastName}"?`}
            </p>
            <div className={styles.confirmActions}>
              <Button type="button" variant="secondary" onClick={() => setConfirmAction(null)}>
                Cancel
              </Button>
              {confirmAction.type === "leave" ? (
                <leaveFetcher.Form
                  method="post"
                  action={`/organizations/${organizationId}/networks/${network.id}/memberships/${confirmAction.membership.id}/leave`}
                >
                  <input
                    type="hidden"
                    name="podcastId"
                    value={confirmAction.membership.podcastId}
                  />
                  <Button
                    type="submit"
                    variant="danger"
                    loading={leaveFetcher.state === "submitting"}
                    loadingText="Removing..."
                  >
                    Remove
                  </Button>
                </leaveFetcher.Form>
              ) : (
                <rejectFetcher.Form
                  method="post"
                  action={`/organizations/${organizationId}/networks/${network.id}/memberships/${confirmAction.membership.id}/reject`}
                >
                  <Button
                    type="submit"
                    variant="danger"
                    loading={rejectFetcher.state === "submitting"}
                    loadingText="Rejecting..."
                  >
                    Reject
                  </Button>
                </rejectFetcher.Form>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
